import React, { Children, cloneElement, isValidElement, ReactElement, ReactNode } from 'react'
import {
  BoxProps,
  Button,
  Card,
  Divider,
  Flex,
  FlexProps,
  Grid,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Spacer,
  Stack,
  useDisclosure,
  useChakraFocusRef,
  visuallyHiddenStyle
} from '~/design_system'
import useGroupChildren from '~/common/childrenUtils'
import { CONTAINER_QUERY_BREAKPOINTS, cq } from '~/common/containerQuery'
import { Msg, useMsg } from '~/common/localizationUtils'
import { ClientOnly } from '~/common/nextJsUtils'
import { $thm } from '~/common/themeSx'
import { Settings } from '~/icons'
import { useIsSanaRebrandEnabled } from '~/theme/useIsSanaRebrandEnabled'
import { DisplayModeButtons } from './ActionBar.DisplayModeButtons'
import { ActionBarFilterTabs } from './ActionBar.FilterTabs'
import { ActionBarResultCount } from './ActionBar.ResultCount'
import { ActionBarSearch } from './ActionBar.Search'
import { SortBy } from './ActionBar.SortBy'
import { ToggleFiltersButton } from './ActionBar.ToggleFiltersButton'
import { VisibleFieldsButton } from './ActionBar.VisibleFieldsButton'

/**
 * AutoActionBar
 * An AutoActionBar component that is used on list pages and will automatically lay out its children.
 * It will also automatically wrap itself in a Card component unless `hideCard` is set to true.
 *
 * The following components are supported:
 * - ActionBarSearch
 * - ActionBarResultCount
 * - ActionBarFilterTabs
 * - SortBy
 * - DisplayModeButtons
 * - VisibleFieldsButton
 * - ToggleFiltersButton
 *
 * A popover will be shown for SortBy and DisplayModeButtons if there is not enough space
 * (i.e. tablets and mobile).
 */
const InnerAutoActionBar = ({ children, ...props }: FlexProps) => {
  const extracted = useGroupChildren(children, {
    search: {
      isComponent: ActionBarSearch,
      isSlot: 'search',
      errorName: '<ActionBarSearch>'
    },
    resultCount: {
      isComponent: ActionBarResultCount,
      isSlot: 'resultCount',
      errorName: '<ActionBarResultCount>'
    },
    filterTabs: {
      isComponent: ActionBarFilterTabs,
      isSlot: 'filterTabs',
      errorName: '<ActionBarFilterTabs>'
    },
    sortBy: {
      isComponent: SortBy,
      isSlot: 'sortBy',
      errorName: '<SortBy>'
    },
    displayModeButtons: {
      isComponent: DisplayModeButtons,
      isSlot: 'displayModeButtons',
      errorName: '<DisplayModeButtons>'
    },
    visibleFieldsButton: {
      isComponent: VisibleFieldsButton,
      isSlot: 'visibleFieldsButton',
      errorName: '<VisibleFieldsButton>'
    }
  })
  const msg = useMsg()
  const isRebrand = useIsSanaRebrandEnabled()

  return (
    <ClientOnly>
      <Card
        {...(isRebrand ? { variant: 'tonal' } : { bg: 'gray.50' })}
        data-action-bar
        data-auto-action-bar
        // Legacy: flat bottom so action joins the bulk/list stack.
        {...(!isRebrand && { roundedBottom: 0 })}
        position="relative"
        aria-label={msg('common.action_bar')}
        sx={$thm({ containerType: 'inline-size', containerName: 'actionBar', py: 4, px: { $base: 4, $canvas: 6 } })}>
        <SkipToFiltersLink />
        <Grid
          data-testid="action-bar"
          sx={cq({
            // Container
            gap: 4,
            gridTemplateAreas: {
              '@actionBar/base': `
                'left'
                'right'
              `,
              '@actionBar/3xl': `
                'left right'
              `
            },
            gridTemplateColumns: {
              '@actionBar/base': '1fr',
              '@actionBar/3xl': `minmax(0px, 400px) ${extracted.filterTabs ? '1fr' : 'auto'}`
            },

            '[data-action-bar-left]': {
              gridArea: 'left'
            },

            '[data-action-bar-right]': {
              gridArea: 'right',
              gap: 4,
              flexWrap: {
                '@actionBar/base': 'wrap',
                '@actionBar/3xl': 'nowrap'
              },
              justifyContent: 'flex-end'
            },

            '[data-spacer-1]': {
              display: {
                '@actionBar/base': 'none',
                '@actionBar/3xl': 'flex'
              }
            },

            // Toggle Filters Button
            '[data-action-bar-toggle-filters]': {},

            // Search
            '[data-action-bar-search]': {
              w: 'full'
            },

            // Result count
            '[data-action-bar-result-count]': {
              // Visually hide the result count on small action bars
              [`@container actionBar (max-width: ${CONTAINER_QUERY_BREAKPOINTS['3xl']}px)`]: {
                '&': visuallyHiddenStyle
              },
              // Visually hide the result count on medium action bars, but only when filter tabs are not present and sort **is** present
              [`@container actionBar (max-width: ${CONTAINER_QUERY_BREAKPOINTS['5xl']}px)`]: {
                '[auto-action-bar]:not(:has([data-action-bar-filter-tabs])):has([data-action-bar-sort]) &':
                  visuallyHiddenStyle
              }
            },

            // Filter tabs
            '[data-action-bar-filter-tabs]': {},

            // Popover Button
            'span:has(> [data-action-bar-popover-button])': {},

            // Sort By
            '[data-action-bar-sort]': {
              flexGrow: {
                '@actionBar/base': extracted.filterTabs ? 'initial' : 1,
                '@actionBar/3xl': 'initial'
              }
            },

            // Display Mode Buttons
            '[data-display-mode-buttons]': {},

            // Visible Fields Button
            'span:has(> [data-visible-fields-button])': {}
          })}
          {...props}>
          <Flex gridArea="left" gap={4} flexWrap="nowrap">
            <ToggleFiltersButton />
            {extracted.search}
            {extracted.resultCount}
          </Flex>
          <Flex data-action-bar-right>
            {extracted.filterTabs && (
              <>
                <Spacer data-spacer-1 />
                {extracted.filterTabs}
                <Spacer data-spacer-2 />
              </>
            )}
            <OverflowIntoPopoverWhenActionBarIsSmallAndCrowded
              shouldOverflow={Boolean(extracted.filterTabs && extracted.sortBy)}>
              {extracted.sortBy}
              {extracted.displayModeButtons}
            </OverflowIntoPopoverWhenActionBarIsSmallAndCrowded>
            {extracted.visibleFieldsButton}
          </Flex>
        </Grid>
      </Card>
    </ClientOnly>
  )
}

export const AutoActionBar = Object.assign(InnerAutoActionBar, {
  ToggleFiltersButton,
  Search: ActionBarSearch,
  FilterTabs: ActionBarFilterTabs,
  ResultCount: ActionBarResultCount,
  SortBy,
  DisplayModeButtons,
  VisibleFieldsButton
})

const OverflowIntoPopoverWhenActionBarIsSmallAndCrowded = ({
  shouldOverflow,
  children
}: {
  shouldOverflow: boolean
  children: ReactNode
}) => {
  const msg = useMsg()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const firstFieldRef = useChakraFocusRef<HTMLElement>()
  const registerRefIfFirst = (el: HTMLElement) => {
    if (firstFieldRef.current === null) {
      // @ts-ignore
      firstFieldRef.current = el
    }
  }
  const childrenArray = Children.toArray(children)

  const showOnSmallerActionBar = cq({
    display: shouldOverflow ? { '@actionBar/base': 'flex', '@actionBar/[1000px]': 'none' } : 'none'
  })
  const hideOnSmallerActionBar = cq({
    display: shouldOverflow ? { '@actionBar/base': 'none', '@actionBar/[1000px]': 'flex' } : 'flex'
  })

  return (
    <>
      <Popover
        variant="responsive"
        isOpen={isOpen}
        initialFocusRef={firstFieldRef}
        onOpen={onOpen}
        onClose={onClose}
        placement="bottom-end">
        <PopoverTrigger>
          <IconButton
            data-action-bar-popover-button
            data-testid="action-popover-button"
            size="md"
            icon={<Settings />}
            variant="secondary"
            aria-label={msg('common.action_bar.toggle_more_button')}
            showTooltip
            sx={{ 'span:has(> &)': { ...showOnSmallerActionBar } }}
          />
        </PopoverTrigger>
        <Portal>
          <PopoverContent data-action-popover-content data-testid="action-popover-content" w="max-content">
            <Stack gap={1} w="max-content" divider={<Divider />}>
              {/* The children, rendered into the Popover on a smaller ActionBar */}
              {childrenArray.map(child => {
                if (!isValidElement(child)) return null
                return cloneElement(child as ReactElement<{ ref?: (el: HTMLElement) => void }>, {
                  ref: registerRefIfFirst
                })
              })}
            </Stack>
          </PopoverContent>
        </Portal>
      </Popover>
      {/* The children, renders into the ActionBar when it's wider */}
      {childrenArray.map(child => {
        if (!isValidElement<{ sx?: BoxProps['sx'] }>(child)) return null
        return cloneElement(child as ReactElement<{ sx?: BoxProps['sx'] }>, {
          sx: { ...child.props.sx, ...hideOnSmallerActionBar }
        })
      })}
    </>
  )
}

function SkipToFiltersLink() {
  return (
    <Button
      size="sm"
      id="skip-to-list-filters-btn"
      onClick={e => {
        e.preventDefault()
        document.getElementById('skip-to-list-results-btn')?.focus()
      }}
      pointerEvents="none"
      alignItems="center"
      position="absolute"
      top="-35px"
      left="4px"
      zIndex="tooltip"
      transform="translateY(25%)"
      transition="transform 0.15s"
      opacity={0}
      bg="white"
      roundedBottom={0}
      _focus={{
        transform: 'translateY(0%)',
        boxShadow: 'outline',
        opacity: 1
      }}
      // On smaller screens the filtersidebar is hidden, so we don't want to show this button
      display={{ base: 'none', lg: 'flex' }}
      scrollMarginTop="500px">
      <Msg id="common.ui.skip_to_filters" />
    </Button>
  )
}
